/* eslint-disable no-useless-escape */
/* eslint-disable import/no-webpack-loader-syntax */
import React from 'react'
import { Code, Link } from 'rebass'

import ButtonLink from '../components/ButtonLink'
import Command from '../components/Command'
import Heading from '../components/Heading'
import P from '../components/P'
import SH from '../components/SH'
import Theory from '../components/Theory'
import Discuss from '../components/Discuss'
import Exercise from '../components/Exercise'
import Toc from '../components/Toc'
import Lost from '../components/Lost'

const IndexPage = () => (
  <div>
    <Toc active="kick off" />
    <Heading fontSize={7}>Kick off</Heading>
    <P>
      Open this website in your browser:{' '}
      <Link href="http://sfreact.limenius.com">
        http://sfreact.limenius.com/
      </Link>
      .
    </P>
    <P>
      The slides for the sections of theory are available at{' '}
      <Link
        href="
        https://www.slideshare.net/nachomartin/server-side-rendering-with-react-and-symfony"
      >
        https://www.slideshare.net/nachomartin/server-side-rendering-with-react-and-symfony
      </Link>
      .
    </P>
    <Theory>
      <P>Introduction to the workshop.</P>
    </Theory>
    <Heading fontSize={5}>A first program</Heading>
    <P>Let's start from the beginning.</P>
    <P>Create a new directory somewhere and init a composer project in it:</P>
    <Command>cd</Command>
    <Command>mkdir sfreact</Command>
    <Command>cd sfreact</Command>
    <Command>composer init</Command>
    <P>
      Answer with the default option (or what you want, it doesn't really
      matter), except for the last two questions:
    </P>
    <Command>
      Would you like to define your dependencies (require) interactively [yes]?
      no
    </Command>
    <Command>
      Would you like to define your dev dependencies (require-dev) interactively
      [yes]? no
    </Command>
    <P>And install this single package:</P>
    <Command>composer require symfony/process</Command>
    <P>
      Let's play with this new project{' '}
      <span role="img" aria-label="party">
        🎉
      </span>
    </P>
    <P>
      Let's write our first SSR code, open a file called <Code>index.php</Code>{' '}
      and add this code:
    </P>
    <SH language="php" code={require('raw-loader!./code/first.php.txt')} />
    <P>Run the embedded server of PHP with</P>
    <Command>php -S localhost:8080</Command>
    <P>
      And visit <Link href="http://localhost:8080">http://localhost:8080</Link>{' '}
      in your browser.
    </P>
    <P>You should see something like</P>
    <SH
      code={`
My program is: "process.stdout.write(eval('1+1;').toString())"
Output: 2
      `}
    />
    <P>Let's examine these parts. They are simple but important:</P>
    <ul>
      <li>
        <Code>eval(myprogram)</Code>
      </li>
      <li>
        <Code>toString()</Code>
      </li>
      <li>
        <Code>process.stdout.write()</Code>
      </li>
    </ul>
    <P>
      This is called a <strong>runner</strong>. This runner has many
      limitations. Let's address them one by one:
    </P>
    <Heading fontSize={5}>Error handling</Heading>
    <Discuss>
      <P>
        What happens if we change <Code>$code</Code> to instead of being{' '}
      </P>
      <Code>"1 + 1"</Code>
      <P> something like </P>
      <Code>"1 + i_am_not_defined"</Code>
      <P>?</P>
      <P> Run your program with</P>
      <P>
        <Code>
          node -e "{'<'}
          program
          {'>'}"
        </Code>{' '}
      </P>
      <P>in the command line, to see what should happen.</P>
    </Discuss>
    <P>Let's add error handling to our little program:</P>
    <SH
      language="php"
      code={`
echo('My program is: '.$embedded.'<br/>');
echo('Exit code: '.$process->getExitCode().'<br/>');
echo('Output: '.$process->getOutput().'<br/>');
echo('Error Output: '.$process->getErrorOutput().'<br/>');`}
    />
    <SH
      language="php"
      code={`
My program is: "process.stdout.write(eval('1+i_am_not_defined;').toString())"
Exit code: 1
Output: 
Error Output: undefined:1 1+i_am_not_defined; ^ ReferenceError: i_am_not_defined is not defined at eval (eval at ([eval]:1:22), :1:3) at [eval]:1:22 at Script.runInThisContext (vm.js:91:20) at Object.runInThisContext (vm.js:298:38) at Object. ([eval]-wrapper:6:22) at Module._compile (internal/modules/cjs/loader.js:689:30) at evalScript (internal/bootstrap/node.js:563:27) at startup (internal/bootstrap/node.js:248:9) at bootstrapNodeJSCore (internal/bootstrap/node.js:596:3)`}
    />
    <P>
      There is much more we could do here, but at least this will give us some
      feedback to move on.
    </P>
    <Heading fontSize={5}>Multiline support</Heading>
    <P>Let's try another silly problem. Can we run this code?</P>
    <SH
      language="php"
      code={`
$code = <<<JS
var sum = function(a, b) {
  return a + b;
}
sum(1, 1);
JS;
      `}
    />
    <Exercise>
      <P>Run this code.</P>
    </Exercise>
    <P>
      The problem is that we are writing a multiline program and running it with{' '}
      <Code>node -e</Code>. We will also experience problems when our program is
      big enough to not fit the command line restrictions (a limitation that
      varies depending on the OS).
    </P>
    <P>We could for instance write a temporary file.</P>
    <P>Let's try this</P>
    <SH language="php" code={require('raw-loader!./code/second.php.txt')} />
    <P>
      Apart from creating a temporary file (that we should take care to remove
      in a real environment), this line has changed{' '}
    </P>
    <P>
      <Code>
        $embedded = '"process.stdout.write(eval(\''.$code.'\').toString())"';
      </Code>
    </P>
    <P>To this:</P>
    <P>
      <Code>
        $embedded =
        'process.stdout.write(eval('.json_encode($code).').toString())';
      </Code>
    </P>
    <Heading fontSize={5}>Variant and Context code</Heading>
    <P>
      Typically we will face this situation. We will want to have two pieces of
      code:
    </P>
    <ul>
      <li>
        A (rather long) program that doesn't change between requests, or{' '}
        <strong>context code</strong>.
      </li>
      <li>
        A (rather short) program that changes between requests, or{' '}
        <strong>variant code</strong>.
      </li>
    </ul>
    <P>Let's express it. Change this part:</P>
    <SH
      language="PHP"
      code={`
$code = <<<JS
var sum = function(a, b) {
  return a + b;
}
sum(1, 1);
JS;
    `}
    />
    <P>To this:</P>
    <SH
      language="PHP"
      code={`
$context = <<<JS
var sum = function(a, b) {
  return a + b;
};
JS;

$variant = <<<JS
sum(1, 1);
JS;

$code = $context . $variant;
`}
    />
    <P>
      This expresses what we will want to do. We will have a (rather big)
      application, and in each request we will want to say "hey, app, render
      this component with these parameters".{' '}
    </P>
    <P>Let's load the context part from a file:</P>
    <SH
      language="PHP"
      code={`
$context = file_get_contents(__DIR__.DIRECTORY_SEPARATOR.'app.js');
    `}
    />
    <P>
      And place the context code in <Code>app.js</Code>:
    </P>
    <SH
      language="js"
      code={`
var sum = function(a, b) {
  return a + b;
};
    `}
    />
    <Heading fontSize={5}>PhpExecJs</Heading>
    <P>
      We could refine our runner more and more... or we can use a library that
      does this for you. Your instructor in this workshop has written such
      library
    </P>
    <P>
      Apart from what we were doing, it will use the PHP extension V8js if it is
      installed.
    </P>
    <P>
      This extension is <i>hard to compile</i>, however, it comes with a huge
      gain in performance and the ability of caching the context between
      requests.
    </P>
    <P>Install it:</P>
    <Command>composer require nacmartin/phpexecjs</Command>
    <P>And rewrite our PHP file to be:</P>
    <SH
      language="PHP"
      code={`
<?php

namespace App;

require __DIR__ . '/vendor/autoload.php';

use Nacmartin\\PhpExecJs\\PhpExecJs;

$phpexecjs = new PhpExecJs();

$phpexecjs->createContextFromFile(__DIR__.DIRECTORY_SEPARATOR.'app.js');

$variant = <<<JS
sum(1, 1);
JS;

$result = $phpexecjs->evalJs($variant);

echo('<html><body>');

echo('Output: '.$result);

echo('</body></html>');
    `}
    />
    <P>Does it still work?</P>

    <P>
      Now we are ready to build our app in the context part. We will want lots
      of things! Let's see:
    </P>
    <ul>
      <li>Dependency management.</li>
      <li>Modern JS or JSX or whatever.</li>
      <li>All bundled in one file.</li>
      <li>Something that we can use for our client side app.</li>
      <li>...and thousand of other things.</li>
    </ul>
    <P>
      There is a tool that does all of this, so let's give it a short
      introduction in the next chapter.
    </P>
    <Lost tag="01-kickoff" />
    <ButtonLink to="/webpack" title="Go to the next section" />
  </div>
)

export default IndexPage



// WEBPACK FOOTER //
// ./src/pages/index.js